import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import './UploadPodModal.css';
import './PodViewer.css';
import FileDropZone from './FileUploader';
import { baseUrl } from '../../../api';

/* ---------- small inline icon helper (no extra dependency needed) ---------- */
const Icon: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
    >
        {children}
    </svg>
);

const ZoomInIcon = () => (
    <Icon>
        <circle cx='11' cy='11' r='7' />
        <path d='M21 21l-4.3-4.3M11 8v6M8 11h6' />
    </Icon>
);
const ZoomOutIcon = () => (
    <Icon>
        <circle cx='11' cy='11' r='7' />
        <path d='M21 21l-4.3-4.3M8 11h6' />
    </Icon>
);
const RotateIcon = () => (
    <Icon>
        <path d='M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5' />
    </Icon>
);
const FitIcon = () => (
    <Icon>
        <path d='M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3' />
    </Icon>
);
const DownloadIcon = () => (
    <Icon>
        <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' />
    </Icon>
);
const ExternalLinkIcon = () => (
    <Icon>
        <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
        <polyline points='15 3 21 3 21 9' />
        <line x1='10' y1='14' x2='21' y2='3' />
    </Icon>
);
const ExternalLinksIcon = () => (
    <Icon>
        <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
        <polyline points='15 3 21 3 21 9' />
        <line x1='10' y1='14' x2='21' y2='3' />
        <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' style={{ opacity: 0.5 }} />
        <polyline points='15 3 21 3 21 9' style={{ opacity: 0.5 }} />
        <line x1='10' y1='14' x2='21' y2='3' style={{ opacity: 0.5 }} />
    </Icon>
);
const ExpandIcon = () => (
    <Icon>
        <path d='M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7' />
    </Icon>
);
const ShrinkIcon = () => (
    <Icon>
        <path d='M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7' />
    </Icon>
);
const ChevronLeftIcon = () => (
    <Icon size={24}>
        <path d='M15 18l-6-6 6-6' />
    </Icon>
);
const ChevronRightIcon = () => (
    <Icon size={24}>
        <path d='M9 18l6-6-6-6' />
    </Icon>
);
const CloseIcon = () => (
    <Icon>
        <path d='M18 6L6 18M6 6l12 12' />
    </Icon>
);
const ImageOffIcon = () => (
    <Icon size={40}>
        <rect x='3' y='3' width='18' height='18' rx='2' />
        <circle cx='9' cy='9' r='1.5' />
        <path d='M21 15l-5-5L5 21' />
    </Icon>
);

/* ------------------------------ viewer ------------------------------ */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

const pad2 = (n: number) => String(n).padStart(2, '0');

interface PodViewerProps {
    images: string[];
    fileNamePrefix: string;
    consignmentNo: string;
    invoiceNo: string;
    expanded: boolean;
    onToggleExpand: () => void;
    isPublicView?: boolean;
    signatureURL?: string;
}

const PodViewer: React.FC<PodViewerProps> = ({ images, fileNamePrefix, consignmentNo, invoiceNo, expanded, onToggleExpand, isPublicView = false, signatureURL }) => {
    const [index, setIndex] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

    // Combine POD images with signature (signature goes last)
    const allImages = signatureURL ? [...images, signatureURL] : images;
    const count = allImages.length;
    const src = allImages[index];
    const isSignature = signatureURL && index === count - 1;

    const resetView = useCallback(() => {
        setZoom(1);
        setRotation(0);
        setOffset({ x: 0, y: 0 });
    }, []);

    const applyZoom = useCallback((next: number) => {
        const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        setZoom(clamped);
        if (clamped <= 1) setOffset({ x: 0, y: 0 });
    }, []);

    const goTo = useCallback(
        (next: number) => {
            if (next < 0 || next >= count) return;
            setIndex(next);
            setStatus('loading');
            resetView();
        },
        [count, resetView]
    );

    /* keyboard shortcuts (Esc is already handled by the Modal) */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowLeft':
                    goTo(index - 1);
                    break;
                case 'ArrowRight':
                    goTo(index + 1);
                    break;
                case '+':
                case '=':
                    applyZoom(zoom + ZOOM_STEP);
                    break;
                case '-':
                    applyZoom(zoom - ZOOM_STEP);
                    break;
                case '0':
                    resetView();
                    break;
                case 'r':
                case 'R':
                    setRotation((r) => (r + 90) % 360);
                    break;
                default:
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [index, zoom, goTo, applyZoom, resetView]);

    /* mouse wheel zoom */
    const onWheel = (e: React.WheelEvent) => {
        applyZoom(zoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    };

    /* drag to pan (only when zoomed in) */
    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (zoom <= 1) return;
        dragRef.current = { startX: e.clientX, startY: e.clientY, originX: offset.x, originY: offset.y };
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const d = dragRef.current;
        if (!d) return;
        setOffset({ x: d.originX + (e.clientX - d.startX), y: d.originY + (e.clientY - d.startY) });
    };
    const endDrag = () => {
        dragRef.current = null;
        setDragging(false);
    };

    const onDoubleClick = () => {
        if (zoom > 1) {
            setZoom(1);
            setOffset({ x: 0, y: 0 });
        } else {
            setZoom(2.5);
        }
    };

    const downloadImage = async () => {
        const ext = /^data:image\/png/.test(src) ? 'png' : 'jpg';
        const fileName = `${fileNamePrefix}-${index + 1}.${ext}`;
        const trigger = (href: string) => {
            const a = document.createElement('a');
            a.href = href;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        };
        try {
            const blob = await (await fetch(src)).blob();
            const url = URL.createObjectURL(blob);
            trigger(url);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            // e.g. cross-origin URL blocked by CORS: open it in a new tab instead
            window.open(src, '_blank', 'noopener');
        }
    };

    const openPublicLink = () => {
        // Since we have a proxy, use relative URL which will be proxied to backend
        // Include the current image index to open the specific image being viewed
        // Use consignmentNo if available, otherwise use invoiceNo
        let publicPodUrl;
        if (consignmentNo) {
            publicPodUrl = `/api/v1/public/pod?consignmentNo=${consignmentNo}&index=${index}`;
        } else if (invoiceNo) {
            publicPodUrl = `/api/v1/public/pod?invoiceNo=${invoiceNo}&index=${index}`;
        } else {
            return; // Neither available, don't open
        }
        window.open(publicPodUrl, '_blank', 'noopener');
    };

    const openAllPublicLinks = () => {
        // Open all images in new tabs
        // Use consignmentNo if available, otherwise use invoiceNo
        if (!consignmentNo && !invoiceNo) {
            return; // Neither available, don't open
        }
        
        for (let i = 0; i < count; i++) {
            let publicPodUrl: string;
            if (consignmentNo) {
                publicPodUrl = `/api/v1/public/pod?consignmentNo=${consignmentNo}&index=${i}`;
            } else {
                publicPodUrl = `/api/v1/public/pod?invoiceNo=${invoiceNo}&index=${i}`;
            }
            // Small delay between opening each tab to prevent browser blocking
            setTimeout(() => {
                window.open(publicPodUrl, '_blank', 'noopener');
            }, i * 200);
        }
    };

    if (!count) {
        return (
            <div className='pv-empty'>
                <ImageOffIcon />
                <p className='pv-empty-title'>No proof of delivery found</p>
                <p className='pv-empty-text'>The driver has not uploaded a delivery photo for this consignment yet.</p>
            </div>
        );
    }

    return (
        <div className='pv-viewer'>
            <div
                className={`pv-stage${zoom > 1 ? ' is-zoomed' : ''}${dragging ? ' is-dragging' : ''}`}
                onWheel={onWheel}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onDoubleClick={onDoubleClick}
            >
                <div className='pv-canvas'>
                    <img
                        key={src}
                        src={src}
                        alt={`Proof of delivery ${index + 1} of ${count}`}
                        className={`pv-img${dragging ? ' no-anim' : ''}${status === 'ready' ? ' is-ready' : ''}`}
                        style={{
                            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                        }}
                        draggable={false}
                        onLoad={() => setStatus('ready')}
                        onError={() => setStatus('error')}
                    />
                </div>

                {status === 'loading' && (
                    <div className='pv-status' role='status'>
                        <span className='pv-spinner' />
                        <span>Loading image</span>
                    </div>
                )}
                {status === 'error' && (
                    <div className='pv-status pv-status-error' role='alert'>
                        <ImageOffIcon />
                        <span>This image could not be loaded</span>
                    </div>
                )}

                {count > 1 && (
                    <div className='pv-counter'>
                        {pad2(index + 1)} / {pad2(count)}{isSignature && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>(Signature)</span>}
                    </div>
                )}

                {count > 1 && (
                    <>
                        <button
                            type='button'
                            className='pv-nav pv-nav-prev'
                            onClick={() => goTo(index - 1)}
                            disabled={index === 0}
                            aria-label='Previous image'
                            onPointerDown={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                        >
                            <ChevronLeftIcon />
                        </button>
                        <button
                            type='button'
                            className='pv-nav pv-nav-next'
                            onClick={() => goTo(index + 1)}
                            disabled={index === count - 1}
                            aria-label='Next image'
                            onPointerDown={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                        >
                            <ChevronRightIcon />
                        </button>
                    </>
                )}

                <div
                    className='pv-toolbar'
                    role='toolbar'
                    aria-label='Image controls'
                    onPointerDown={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                    onWheel={(e) => e.stopPropagation()}
                >
                    <button
                        type='button'
                        className='pv-btn'
                        onClick={() => applyZoom(zoom - ZOOM_STEP)}
                        disabled={zoom <= MIN_ZOOM}
                        aria-label='Zoom out'
                        title='Zoom out (-)'
                    >
                        <ZoomOutIcon />
                    </button>
                    <span className='pv-zoom-label' aria-live='polite'>
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        type='button'
                        className='pv-btn'
                        onClick={() => applyZoom(zoom + ZOOM_STEP)}
                        disabled={zoom >= MAX_ZOOM}
                        aria-label='Zoom in'
                        title='Zoom in (+)'
                    >
                        <ZoomInIcon />
                    </button>
                    <span className='pv-sep' />
                    <button type='button' className='pv-btn' onClick={resetView} aria-label='Fit to screen' title='Fit to screen (0)'>
                        <FitIcon />
                    </button>
                    <button
                        type='button'
                        className='pv-btn'
                        onClick={onToggleExpand}
                        aria-label={expanded ? 'Exit full screen' : 'Full screen'}
                        title={expanded ? 'Exit full screen' : 'Full screen'}
                    >
                        {expanded ? <ShrinkIcon /> : <ExpandIcon />}
                    </button>
                    <button
                        type='button'
                        className='pv-btn'
                        onClick={() => setRotation((r) => (r + 90) % 360)}
                        aria-label='Rotate 90 degrees'
                        title='Rotate (R)'
                    >
                        <RotateIcon />
                    </button>
                    <span className='pv-sep' />
                    <button type='button' className='pv-btn' onClick={downloadImage} aria-label='Download image' title='Download'>
                        <DownloadIcon />
                    </button>
                    {(consignmentNo || invoiceNo) && (
                        <>
                            <button type='button' className='pv-btn' onClick={openPublicLink} aria-label='Open public link for current image' title='Open public link for current image'>
                                <ExternalLinkIcon />
                            </button>
                            {/* {count > 1 && (
                                <button type='button' className='pv-btn' onClick={openAllPublicLinks} aria-label='Open all public links' title='Open all images in new tabs'>
                                    <ExternalLinksIcon />
                                </button>
                            )} */}
                        </>
                    )}
                </div>
            </div>

            {count > 1 && (
                <div className='pv-thumbs' role='tablist' aria-label='Delivery photos'>
                    {allImages.map((item, i) => {
                        const thumbIsSignature = signatureURL && i === count - 1;
                        return (
                            <button
                                key={i}
                                type='button'
                                role='tab'
                                aria-selected={i === index}
                                aria-label={`Show image ${i + 1}${thumbIsSignature ? ' (Signature)' : ''}`}
                                className={`pv-thumb${i === index ? ' active' : ''}`}
                                onClick={() => goTo(i)}
                                style={{ position: 'relative' }}
                            >
                                <img src={item} alt='' draggable={false} />
                                {thumbIsSignature && (
                                    <span style={{
                                        position: 'absolute',
                                        bottom: '2px',
                                        right: '2px',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: '2px 4px',
                                        borderRadius: '2px'
                                    }}>
                                        Sig
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ------------------------------ modal ------------------------------ */
export const UploadPodModal: React.FC<any> = ({
    title,
    pODUploadConsignmentId,
    consignmentNo,
    invoiceNo,
    type,
    podImages,
    reFetchConsignmentData,
    showModal,
    showPopupHandler = (item: boolean) => {
        return item;
    },
    signatureURL,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setIsLoading(showModal);
        setExpanded(false);
    }, [showModal]);

    const onClose = () => {
        setIsLoading(false);
        showPopupHandler(isLoading);
    };

    const isView = type === 'view';
    const images: string[] = Array.isArray(podImages) ? podImages.filter(Boolean) : [];

    if (!isView) {
        // Upload flow: unchanged
        return (
            <Modal show={isLoading} onHide={onClose} contentClassName='modal-w-xxl'>
                <Modal.Header>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className='modal-body'>
                    <div className='table-parent'>
                        <FileDropZone
                            reFetchConsignmentData={reFetchConsignmentData}
                            id={pODUploadConsignmentId}
                            hideModal={onClose}
                        />
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <Modal
            show={isLoading}
            onHide={onClose}
            centered
            dialogClassName={`pv-dialog${expanded ? ' pv-dialog--full' : ''}`}
            contentClassName={`pv-content${expanded ? ' pv-content--full' : ''}`}
        >
            <div className='pv-header'>
                <div className='pv-heading'>
                    <h5 className='pv-title'>Proof of Delivery</h5>
                    {images.length > 0 && (
                        <span className='pv-subtitle'>
                            {images.length} attachment{images.length > 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                <button type='button' className='pv-btn pv-close' onClick={onClose} aria-label='Close'>
                    <CloseIcon />
                </button>
            </div>
            <Modal.Body className='pv-body'>
                <PodViewer
                    images={images}
                    fileNamePrefix={`POD-${pODUploadConsignmentId || 'consignment'}`}
                    consignmentNo={consignmentNo || ''}
                    invoiceNo={invoiceNo || ''}
                    expanded={expanded}
                    onToggleExpand={() => setExpanded((v) => !v)}
                    signatureURL={signatureURL}
                />
            </Modal.Body>
        </Modal>
    );
};